// MockDesignModal.tsx - AI Mock Design Template
import React, { useState, useEffect } from 'react';
import { MockDesignModalProps, MockType, DocType } from '../../types';
import axios from 'axios';

const MockDesignModal: React.FC<MockDesignModalProps> = ({
    isOpen,
    onClose,
    selectedAgent,
    mockDesign,
    onSave
}) => {
    const [formData, setFormData] = useState({
        name: '',
        type: 'AI' as MockType,
        interfaceDoc: {
            type: 'text' as DocType,
            content: '',
            fileName: ''
        },
        functionalDoc: {
            type: 'text' as DocType,
            content: '',
            fileName: ''
        },
        isActive: true
    });

    const [interfaceFile, setInterfaceFile] = useState<File | null>(null);
    const [functionalFile, setFunctionalFile] = useState<File | null>(null);

    // Initialize form when editing existing mock
    useEffect(() => {
        if (mockDesign) {
            setFormData({
                name: mockDesign.name,
                type: mockDesign.type,
                interfaceDoc: mockDesign.interfaceDoc,
                functionalDoc: mockDesign.functionalDoc,
                isActive: mockDesign.isActive
            });
        } else {
            // Reset form for new mock
            setFormData({
                name: '',
                type: 'AI',
                interfaceDoc: { type: 'text', content: '', fileName: '' },
                functionalDoc: { type: 'text', content: '', fileName: '' },
                isActive: true
            });
        }
    }, [mockDesign, isOpen]);

    const handleFileUpload = (file: File, docType: 'interface' | 'functional') => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target?.result as string;
            if (docType === 'interface') {
                setFormData(prev => ({
                    ...prev,
                    interfaceDoc: {
                        type: 'upload',
                        content: content,
                        fileName: file.name
                    }
                }));
                setInterfaceFile(file);
            } else {
                setFormData(prev => ({
                    ...prev,
                    functionalDoc: {
                        type: 'upload',
                        content: content,
                        fileName: file.name
                    }
                }));
                setFunctionalFile(file);
            }
        };
        reader.readAsText(file);
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            alert('Please provide a mock name');
            return;
        }

        if (formData.interfaceDoc.type === 'text' && !formData.interfaceDoc.content.trim()) {
            alert('Please provide interface documentation');
            return;
        }

        // Create mock template structure
        const mockTemplate = {
            type: formData.type === 'AI' ? 'api' : 'api',
            endpoints: {},
            generated_from: 'ui',
            interface_doc: formData.interfaceDoc,
            functional_doc: formData.functionalDoc
        };

        // If Manual type, try to parse the interface doc to create endpoints
        if (formData.interfaceDoc.content) {
            try {
                const parsed = JSON.parse(formData.interfaceDoc.content);
                if (parsed.endpoints) {
                    mockTemplate.endpoints = parsed.endpoints;
                }
            } catch (e) {
                // Not JSON, will need AI processing
            }
        }

        // Call backend to save mock template
        try {
            const response = await axios.post(`${API_BASE_URL}/api/mock-templates`, {
                agent_name: selectedAgent,
                mock_name: formData.name,
                mock_type: 'api',
                mock_source: 'ui_created',
                mock_template: mockTemplate,
                is_active: formData.isActive
            });

            if (response.data.success) {
                onSave({
                    ...formData,
                    id: response.data.id,
                    agentName: selectedAgent
                });
            }
        } catch (error) {
            console.error('Failed to save mock template:', error);
            alert('Failed to save mock template');
            return;
        }

        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '800px', maxWidth: '90vw' }}>
                <h2> {mockDesign ? 'Edit' : 'Create'} AI Mock Design</h2>

                <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <strong>Mock for Agent: {selectedAgent}</strong> (readonly)
                </div>

                {/* 1. Mock Name */}
                <div className="control-group">
                    <label>1. Mock Name <span style={{ color: 'red' }}>*</span></label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., PaymentServiceMock, OrderAPIService"
                        style={{ width: '100%' }}
                    />
                </div>

                {/* 2. Mock Type */}
                <div className="control-group">
                    <label>2. Mock Type</label>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="radio"
                                name="mockType"
                                value="AI"
                                checked={formData.type === 'AI'}
                                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as MockType }))}
                            />
                            AI Mock (Dynamic) - AI generates responses based on documentation
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="radio"
                                name="mockType"
                                value="Manual"
                                checked={formData.type === 'Manual'}
                                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as MockType }))}
                            />
                            Manual Mock (Predefined) - Static responses, rule-based
                        </label>
                    </div>
                </div>

                {/* 3. Interface Documentation */}
                <div className="control-group">
                    <label>3. Interface Documentation <span style={{ color: 'red' }}>*</span></label>
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="radio"
                                name="interfaceDocType"
                                value="upload"
                                checked={formData.interfaceDoc.type === 'upload'}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    interfaceDoc: { ...prev.interfaceDoc, type: e.target.value as DocType }
                                }))}
                            />
                            Upload File
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="radio"
                                name="interfaceDocType"
                                value="url"
                                checked={formData.interfaceDoc.type === 'url'}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    interfaceDoc: { ...prev.interfaceDoc, type: e.target.value as DocType }
                                }))}
                            />
                            URL
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="radio"
                                name="interfaceDocType"
                                value="text"
                                checked={formData.interfaceDoc.type === 'text'}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    interfaceDoc: { ...prev.interfaceDoc, type: e.target.value as DocType }
                                }))}
                            />
                            Text Input
                        </label>
                    </div>

                    {formData.interfaceDoc.type === 'upload' && (
                        <div style={{ border: '2px dashed #ddd', padding: '1rem', borderRadius: '4px', textAlign: 'center' }}>
                            <input
                                type="file"
                                accept=".json,.txt,.md,.yaml,.yml,.xml"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleFileUpload(file, 'interface');
                                }}
                                style={{ marginBottom: '0.5rem' }}
                            />
                            <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                Supported: JSON, TXT, MD, YAML, XML
                                {formData.interfaceDoc.fileName && (
                                    <div style={{ color: '#28a745', marginTop: '0.5rem' }}>
                                        Uploaded: {formData.interfaceDoc.fileName}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {formData.interfaceDoc.type === 'url' && (
                        <input
                            type="url"
                            value={formData.interfaceDoc.content}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                interfaceDoc: { ...prev.interfaceDoc, content: e.target.value }
                            }))}
                            placeholder="https://docs.api.com/interface or https://confluence.com/page"
                            style={{ width: '100%' }}
                        />
                    )}

                    {formData.interfaceDoc.type === 'text' && (
                        <textarea
                            value={formData.interfaceDoc.content}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                interfaceDoc: { ...prev.interfaceDoc, content: e.target.value }
                            }))}
                            placeholder="Paste your API interface documentation here..."
                            rows={6}
                            style={{ width: '100%' }}
                        />
                    )}
                </div>

                {/* 4. Functional Documentation */}
                <div className="control-group">
                    <label>4. Functional Documentation</label>
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="radio"
                                name="functionalDocType"
                                value="upload"
                                checked={formData.functionalDoc.type === 'upload'}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    functionalDoc: { ...prev.functionalDoc, type: e.target.value as DocType }
                                }))}
                            />
                            Upload File
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="radio"
                                name="functionalDocType"
                                value="url"
                                checked={formData.functionalDoc.type === 'url'}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    functionalDoc: { ...prev.functionalDoc, type: e.target.value as DocType }
                                }))}
                            />
                            URL
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="radio"
                                name="functionalDocType"
                                value="text"
                                checked={formData.functionalDoc.type === 'text'}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    functionalDoc: { ...prev.functionalDoc, type: e.target.value as DocType }
                                }))}
                            />
                            Text Input
                        </label>
                    </div>

                    {formData.functionalDoc.type === 'upload' && (
                        <div style={{ border: '2px dashed #ddd', padding: '1rem', borderRadius: '4px', textAlign: 'center' }}>
                            <input
                                type="file"
                                accept=".json,.txt,.md,.yaml,.yml,.xml,.pdf"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleFileUpload(file, 'functional');
                                }}
                                style={{ marginBottom: '0.5rem' }}
                            />
                            <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                Supported: JSON, TXT, MD, YAML, XML, PDF
                                {formData.functionalDoc.fileName && (
                                    <div style={{ color: '#28a745', marginTop: '0.5rem' }}>
                                        Uploaded: {formData.functionalDoc.fileName}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {formData.functionalDoc.type === 'url' && (
                        <input
                            type="url"
                            value={formData.functionalDoc.content}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                functionalDoc: { ...prev.functionalDoc, content: e.target.value }
                            }))}
                            placeholder="https://jira.com/ticket or https://confluence.com/functional-spec"
                            style={{ width: '100%' }}
                        />
                    )}

                    {formData.functionalDoc.type === 'text' && (
                        <textarea
                            value={formData.functionalDoc.content}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                functionalDoc: { ...prev.functionalDoc, content: e.target.value }
                            }))}
                            placeholder="Describe functional behavior, business rules, edge cases..."
                            rows={4}
                            style={{ width: '100%' }}
                        />
                    )}
                </div>

                {/* Mock Status */}
                <div className="control-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                        />
                        Activate mock immediately
                    </label>
                </div>

                <div className="modal-actions">
                    <button onClick={onClose} className="pushable push-secondary">
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">Cancel</span>
                    </button>

                    {formData.type === 'AI' && (
                        <button
                            onClick={handleSave}
                            className="pushable push-warning"
                            disabled={!formData.name.trim()}
                        >
                            <span className="shadow"></span>
                            <span className="edge"></span>
                            <span className="front">Generate AI Mock</span>
                        </button>
                    )}

                    <button
                        onClick={handleSave}
                        className="pushable push-success"
                        disabled={!formData.name.trim()}
                    >
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">Save Mock</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MockDesignModal;